import React from 'react';
import { Box, Typography, Paper, Tooltip, Chip } from '@mui/material';

/* ─── Types ─── */

type CellStatus = 'red' | 'yellow' | null;

interface FacilityColumn {
  ccn: string;
  shortName: string;
}

interface GridSignal {
  id: string;
  name: string;
  category: string;
  isBinary: boolean;
}

interface CheckEngineGridProps {
  groupId?: string;
}

/* ─── Stub Data ─── */

const STUB_FACILITIES: FacilityColumn[] = [
  { ccn: '235001', shortName: 'Maple Grove HC' },
  { ccn: '235042', shortName: 'Lakeview Manor' },
  { ccn: '235089', shortName: 'Pine Ridge Care' },
  { ccn: '235117', shortName: 'Oakwood NH' },
  { ccn: '235203', shortName: 'Cedar Springs' },
];

const GRID_SIGNALS: GridSignal[] = [
  // Hidden Related Party
  { id: 'ins_premium', name: 'Insurance Premium/Bed', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_rent', name: 'Hidden RP: Rent', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_admin', name: 'Hidden RP: Admin', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_therapy', name: 'Hidden RP: Therapy', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_drugs', name: 'Hidden RP: Drugs', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_snf', name: 'Hidden RP: SNF Inpatient', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_benefits', name: 'Hidden RP: Benefits', category: 'Hidden Related Party', isBinary: false },
  { id: 'hidden_rp_dietary', name: 'Hidden RP: Dietary', category: 'Hidden Related Party', isBinary: false },
  // RP YoY
  { id: 'rp_rent_yoy', name: 'RP Real Estate YoY Spike', category: 'RP Year-over-Year', isBinary: false },
  { id: 'rp_admin_yoy', name: 'RP Admin/Mgmt YoY Spike', category: 'RP Year-over-Year', isBinary: false },
  // Balance Sheet
  { id: 'due_from_to', name: 'Due From/To Other Funds', category: 'Balance Sheet', isBinary: false },
  { id: 'notes_loans_zero', name: 'Notes & Loans = $0', category: 'Balance Sheet', isBinary: true },
  { id: 'loans_from_owners', name: 'Loans FROM Owners', category: 'Balance Sheet', isBinary: true },
  { id: 'due_from_owners', name: 'Due FROM Owners', category: 'Balance Sheet', isBinary: true },
  // Staffing Integrity
  { id: 'rn_lpn_wage', name: 'RN/LPN Wage Proximity', category: 'Staffing Integrity', isBinary: false },
  { id: 'contract_labor', name: 'Contract Labor Premium', category: 'Staffing Integrity', isBinary: false },
  { id: 'low_rn_rate', name: 'Low RN Hourly Rate', category: 'Staffing Integrity', isBinary: false },
  // { id: 'cr_vs_pbj', name: 'CR vs PBJ Hours Gap', category: 'Staffing Integrity', isBinary: true },  // Hidden — signal needs rework
];

// Stub matrix: signal_id -> ccn -> status (most recent year)
const STUB_MATRIX: Record<string, Record<string, CellStatus>> = {
  ins_premium:       { '235001': 'red',    '235042': 'yellow', '235089': 'red',    '235117': null,     '235203': 'yellow' },
  hidden_rp_rent:    { '235001': 'red',    '235042': 'red',    '235089': 'red',    '235117': 'yellow', '235203': 'red'    },
  hidden_rp_admin:   { '235001': 'red',    '235042': null,     '235089': 'yellow', '235117': null,     '235203': 'red'    },
  hidden_rp_therapy: { '235001': 'yellow', '235042': null,     '235089': null,     '235117': null,     '235203': null     },
  hidden_rp_drugs:   { '235001': 'yellow', '235042': null,     '235089': null,     '235117': null,     '235203': null     },
  hidden_rp_snf:     { '235001': null,     '235042': null,     '235089': null,     '235117': null,     '235203': null     },
  hidden_rp_benefits:{ '235001': 'red',    '235042': 'yellow', '235089': 'red',    '235117': null,     '235203': 'yellow' },
  hidden_rp_dietary: { '235001': null,     '235042': null,     '235089': null,     '235117': null,     '235203': null     },
  rp_rent_yoy:       { '235001': null,     '235042': 'red',    '235089': null,     '235117': 'yellow', '235203': null     },
  rp_admin_yoy:      { '235001': 'yellow', '235042': null,     '235089': 'red',    '235117': null,     '235203': null     },
  due_from_to:       { '235001': 'red',    '235042': 'yellow', '235089': 'red',    '235117': 'red',    '235203': 'yellow' },
  notes_loans_zero:  { '235001': 'red',    '235042': 'red',    '235089': 'red',    '235117': 'red',    '235203': 'red'    },
  loans_from_owners: { '235001': null,     '235042': 'red',    '235089': null,     '235117': null,     '235203': 'red'    },
  due_from_owners:   { '235001': 'red',    '235042': null,     '235089': 'red',    '235117': null,     '235203': null     },
  rn_lpn_wage:       { '235001': 'yellow', '235042': 'red',    '235089': null,     '235117': 'yellow', '235203': null     },
  contract_labor:    { '235001': 'red',    '235042': null,     '235089': 'yellow', '235117': null,     '235203': 'red'    },
  low_rn_rate:       { '235001': null,     '235042': 'yellow', '235089': null,     '235117': 'red',    '235203': null     },
  cr_vs_pbj:         { '235001': 'red',    '235042': null,     '235089': 'red',    '235117': null,     '235203': null     },
};

/* ─── Helpers ─── */

const CATEGORY_ORDER = ['Hidden Related Party', 'RP Year-over-Year', 'Balance Sheet', 'Staffing Integrity'];

const CATEGORY_COLORS: Record<string, string> = {
  'Hidden Related Party': '#1565c0',
  'RP Year-over-Year': '#6a1b9a',
  'Balance Sheet': '#e65100',
  'Staffing Integrity': '#2e7d32',
};

function facilityRedCount(ccn: string): number {
  return GRID_SIGNALS.reduce((sum, s) => sum + (STUB_MATRIX[s.id]?.[ccn] === 'red' ? 1 : 0), 0);
}

function facilityYellowCount(ccn: string): number {
  return GRID_SIGNALS.reduce((sum, s) => sum + (STUB_MATRIX[s.id]?.[ccn] === 'yellow' ? 1 : 0), 0);
}

function signalRedCount(signalId: string): number {
  return STUB_FACILITIES.reduce((sum, f) => sum + (STUB_MATRIX[signalId]?.[f.ccn] === 'red' ? 1 : 0), 0);
}

/* ─── Grid Dot ─── */

const GridDot: React.FC<{ status: CellStatus }> = ({ status }) => {
  if (status === null) {
    return <Box sx={{ width: 12, height: 12, mx: 'auto' }} />;
  }
  const color = status === 'red' ? '#d32f2f' : '#fbc02d';
  return (
    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, mx: 'auto', boxShadow: `0 1px 3px ${color}55` }} />
  );
};

/* ─── Main Component ─── */

const CheckEngineGrid: React.FC<CheckEngineGridProps> = () => {
  const facilities = STUB_FACILITIES;
  const signals = GRID_SIGNALS;

  const totalFlags = Object.values(STUB_MATRIX).reduce(
    (sum, row) => sum + Object.values(row).filter(v => v === 'red' || v === 'yellow').length,
    0,
  );
  const totalCells = signals.length * facilities.length;
  const densityPct = Math.round((totalFlags / totalCells) * 100);
  const totalRed = Object.values(STUB_MATRIX).reduce(
    (sum, row) => sum + Object.values(row).filter(v => v === 'red').length,
    0,
  );

  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    signals: signals.filter(s => s.category === cat),
  }));

  const facColWidth = 90;
  const labelColWidth = 220;
  const rowFooterWidth = 60;
  const totalWidth = labelColWidth + facilities.length * facColWidth + rowFooterWidth;

  return (
    <Box>
      {/* Summary */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 140px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Flag Density
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ color: densityPct >= 40 ? '#d32f2f' : densityPct >= 20 ? '#ed6c02' : '#2e7d32' }}>
            {densityPct}%
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {totalFlags} flags / {totalCells} cells
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Total Red Alerts
          </Typography>
          <Typography variant="h4" fontWeight={700} color="error">{totalRed}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Facilities
          </Typography>
          <Typography variant="h4" fontWeight={700}>{facilities.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, borderRadius: 2, flex: '1 1 120px' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            Signals Tracked
          </Typography>
          <Typography variant="h4" fontWeight={700}>{signals.length}</Typography>
        </Paper>
      </Box>

      {/* Grid */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Box sx={{ minWidth: totalWidth }}>
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${labelColWidth}px repeat(${facilities.length}, ${facColWidth}px) ${rowFooterWidth}px`,
              bgcolor: '#f5f5f5',
              borderBottom: '2px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 1,
              alignItems: 'end',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Signal
            </Typography>
            {facilities.map(f => (
              <Tooltip key={f.ccn} title={f.shortName} arrow>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', lineHeight: 1.2, display: 'block' }}>
                    {f.ccn}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', lineHeight: 1.1, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: facColWidth - 8 }}>
                    {f.shortName}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
            <Typography variant="caption" fontWeight={700} align="center" sx={{ fontSize: '0.6rem', textTransform: 'uppercase' }}>
              Total
            </Typography>
          </Box>

          {/* Signal rows grouped by category */}
          {grouped.map(({ category, signals: catSignals }) => (
            <Box key={category}>
              {/* Category divider */}
              <Box sx={{ px: 1.5, py: 0.5, bgcolor: `${CATEGORY_COLORS[category]}0A`, borderBottom: '1px solid', borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: CATEGORY_COLORS[category], textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5 }}>
                  {category}
                </Typography>
              </Box>

              {catSignals.map((signal, idx) => {
                const redCount = signalRedCount(signal.id);
                return (
                  <Box
                    key={signal.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `${labelColWidth}px repeat(${facilities.length}, ${facColWidth}px) ${rowFooterWidth}px`,
                      px: 1.5,
                      py: 0.6,
                      alignItems: 'center',
                      borderBottom: idx < catSignals.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {signal.name}
                    </Typography>
                    {facilities.map(f => (
                      <Box key={f.ccn} sx={{ textAlign: 'center' }}>
                        <GridDot status={STUB_MATRIX[signal.id]?.[f.ccn] ?? null} />
                      </Box>
                    ))}
                    <Typography variant="caption" fontWeight={700} align="center" sx={{ color: redCount > 0 ? '#d32f2f' : 'text.disabled', fontSize: '0.7rem' }}>
                      {redCount > 0 ? redCount : '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Footer: per-facility totals */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${labelColWidth}px repeat(${facilities.length}, ${facColWidth}px) ${rowFooterWidth}px`,
              px: 1.5,
              py: 1,
              bgcolor: '#f5f5f5',
              borderTop: '2px solid',
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Facility Totals
            </Typography>
            {facilities.map(f => {
              const red = facilityRedCount(f.ccn);
              const yellow = facilityYellowCount(f.ccn);
              return (
                <Box key={f.ccn} sx={{ textAlign: 'center' }}>
                  {red > 0 && (
                    <Chip label={`${red}R`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#ffebee', color: '#d32f2f', mb: 0.25 }} />
                  )}
                  {yellow > 0 && (
                    <Chip label={`${yellow}Y`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fff8e1', color: '#f9a825' }} />
                  )}
                  {red === 0 && yellow === 0 && (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </Box>
              );
            })}
            <Box />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CheckEngineGrid;
